import {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@ember-data/model';
import type { Type } from '@warp-drive/core-types/symbols';
import nameSort from 'scaleflow/utils/nameSort';
import type BookmarkModel from './bookmark';
import type DepartmentModel from './department';
import type GoalModel from './goal';
import type InitiativeModel from './initiative';
import type LlModel from './ll';
import OwnerModel from './owner';
import type PersonModel from './person';
import type QuestionModel from './question';
import type TfoModel from './tfo';
import type UncertaintyModel from './uncertainty';
import type WfaModel from './wfa';

export default class TeamModel extends OwnerModel {
  declare [Type]: 'team';
  @hasMany('question', {
    async: true,
    inverse: 'team',
  })
  declare questions: AsyncHasMany<QuestionModel>;
  @hasMany('tfo', {
    async: true,
    inverse: 'team',
  })
  declare tfos: AsyncHasMany<TfoModel>;
  @hasMany('ll', {
    async: true,
    inverse: 'team',
  })
  declare lls: AsyncHasMany<LlModel>;
  @hasMany('wfa', {
    async: true,
    inverse: 'team',
  })
  declare wfas: AsyncHasMany<WfaModel>;
  @hasMany('uncertainty', {
    async: true,
    inverse: 'team',
  })
  declare uncertainties: AsyncHasMany<UncertaintyModel>;

  @hasMany('bookmark', {
    async: true,
    inverse: 'team',
  })
  declare bookmarks: AsyncHasMany<BookmarkModel>;
  @hasMany('initiative', {
    async: true,
    inverse: 'teams',
  })
  declare initiatives: AsyncHasMany<InitiativeModel>;

  @belongsTo('department', {
    async: true,
    inverse: 'teams',
  })
  declare department: AsyncBelongsTo<DepartmentModel>;
  @attr('string') declare name: string;
  @attr('string') declare icon: string;
  @attr('boolean') declare show_uncertainties: boolean;
  @attr('boolean') declare show_wfa: boolean;
  @attr('boolean', { defaultValue: true }) declare active: boolean;
  @attr('boolean') declare favorite: boolean;
  @attr('number') declare ftes: number;
  @attr('string', {
    defaultValue: '08:30',
  })
  declare archive_time: string;

  @hasMany('person', {
    async: true,
    inverse: 'teams',
  })
  declare people: AsyncHasMany<PersonModel>;

  get title() {
    return this.name;
  }

  get route() {
    return 'operations.teams.team.check-in';
  }

  get activeMembers() {
    return (this.people.content ?? []).filter((person) => person.active);
  }

  get combinedGoalsFromInitiativesAndTeam() {
    const initiatives = this.initiatives.content ?? [];
    const goalIdsSet = new Set<string | null>();
    initiatives.forEach((initiative) => {
      (initiative.goals.content ?? []).forEach((goal) => {
        goalIdsSet.add(goal.id);
      });
    });
    (this.goals.content ?? []).forEach((goal) => {
      goalIdsSet.add(goal.id);
    });
    return Array.from(goalIdsSet)
      .map((id) => this.store.peekRecord<GoalModel>('goal', id as string))
      .filter((goal) => !!goal);
  }

  get recentInitiatives() {
    return (this.initiatives.content ?? [])?.filter(
      (i) => i.status === 'COMPLETED' || i.status === 'ACTIVE',
    );
  }

  get nowNextInitiatives() {
    const initiatives = this.initiatives.content ?? [];
    const nowNextInitiatives = initiatives.filter((initiative) =>
      ['ACTIVE', 'READY FOR PLANNING', 'READY'].includes(initiative.status),
    );
    return nowNextInitiatives;
  }

  get sortedGoals() {
    const goals = this.goals.content ?? [];
    return [...goals].sort((a, b) => nameSort(a.title, b.title));
  }
}
